import MuiIconButton from '@mui/material/IconButton';
import { alpha, type Theme } from '@mui/material/styles';
import { forwardRef, type AriaAttributes, type MouseEvent, type ReactNode, type Ref } from 'react';

export interface IconButtonProps
  extends Pick<AriaAttributes, 'aria-label' | 'aria-haspopup' | 'aria-expanded'> {
  onClick?: () => void;
  // Keep the click from reaching an ancestor handler (e.g. a click-to-open row that
  // wraps the button). The raw event stays inside the facade — callers pass a boolean,
  // never a MouseEvent.
  stopPropagation?: boolean;
  size?: 'small' | 'medium' | 'large';
  // Set it and the control becomes a link instead of a button. The facade — not the caller —
  // owns target and rel, so no view can ship an external link without them: noreferrer keeps
  // this URL out of the destination's access logs (the panel's own URL carries the «Usuario»'s
  // phone and name in its ?shell= state, and the container sets no Referrer-Policy), and
  // noopener denies the opened page a handle on the tab it came from.
  href?: string;
  // Tints the glyph, leaving the control's background alone. 'primary' is how an inline
  // affordance is made findable without giving it a container of its own — the brand colour
  // resolves from the theme here, so no call site ever writes one. 'error' is the sanction, and it
  // resolves to the DARK tone for the reason written over `errorSx` below.
  color?: 'default' | 'primary' | 'error';
  // Gives the control its own hairline circle. The default `plain` glyph reads as an affordance
  // only while the pointer is over it, which is enough beside a heading it belongs to; `outlined`
  // is for a control that floats over open space (the carril's step buttons) and has to be findable
  // — and disable-able — at rest.
  variant?: 'plain' | 'outlined';
  // Inert while a write it belongs to is in flight — the «×» that folds an inline panel away must
  // not cancel a request the agent can no longer take back.
  disabled?: boolean;
  // For a glyph-only control that RUNS something: it has no label to swap and no room for a spinner
  // beside it, so MUI puts the indicator in place of the glyph and disables the control with it —
  // which is the half that matters, because a second click must not fire a second request. Named
  // and typed like `Button`'s, its sibling.
  //
  // It is declared rather than left to the props spread below. Everything this wrapper does not
  // destructure reaches MUI anyway through `aria`, so `loading` already WORKED before it was
  // declared — and that is precisely the accident the façade exists to prevent: an undeclared prop
  // is not offered by the type system, is not checked, and disappears the day that spread is
  // narrowed. The hole is for aria attributes; behaviour goes through the contract.
  loading?: boolean;
  children?: ReactNode;
}

// The outlined variant's own chrome: MUI's IconButton has no variant, so the circle is drawn here
// from the same tokens Button's `neutral` uses (the ink at 23%), never a hardcoded hex. Disabled
// fades the hairline with the glyph, so the control does not stay drawn as if it were live.
const outlinedSx = (theme: Theme) => ({
  border: '1px solid',
  borderColor: alpha(theme.palette.text.primary, 0.23),
  '&:hover': { borderColor: theme.palette.text.primary },
  '&.Mui-disabled': { borderColor: theme.palette.action.disabled },
});

// The sanction tint. MUI would paint the role `error.main` (#F26279), which is 3.09:1 on white —
// and on a GLYPH-ONLY control the icon is the whole label: there is no word beside it to carry the
// meaning if the tone washes out. `.dark` is 5.77:1 and the theme derives it from `.main` on its
// own, so this adds no token. Same mapping, and the same reason, as `Button`'s own error role.
const errorSx = (theme: Theme) => ({ color: theme.palette.error.dark });

// forwardRef: callers anchor popovers (e.g. a menu) to the real element, so
// the ref has to reach through the wrapper.
export const IconButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, IconButtonProps>(
  function IconButton(
    {
      onClick,
      stopPropagation = false,
      size = 'medium',
      href,
      color = 'default',
      variant = 'plain',
      disabled = false,
      loading = false,
      children,
      ...aria
    },
    ref,
  ) {
    // An ARRAY so the frame and the tint compose — they are orthogonal, and either can be absent.
    // Still nothing at all for a plain, uncoloured caller, so its class list is the one it has today.
    const sx = [
      ...(variant === 'outlined' ? [outlinedSx] : []),
      ...(color === 'error' ? [errorSx] : []),
    ];
    const chrome = sx.length > 0 ? { sx } : {};
    // Attach a handler when there is something to do — a caller may want the stop even
    // without an onClick.
    const handleClick =
      onClick || stopPropagation
        ? (event: MouseEvent<HTMLElement>) => {
            if (stopPropagation) event.stopPropagation();
            onClick?.();
          }
        : undefined;
    if (href !== undefined) {
      return (
        <MuiIconButton
          // Caller props FIRST so the facade's own attributes below cannot be overridden. Anything
          // not destructured out of the props lands in `aria` — including target and rel, which are
          // absent from IconButtonProps and so survive a props-forwarding spread that the type
          // system waves through (excess-property checks only bite on fresh object literals).
          // Spread last and a caller could strip noreferrer, putting the panel's own URL — which
          // carries the «Usuario»'s phone and name in its ?shell= state — into the destination's
          // access logs.
          {...aria}
          // The union ref is this wrapper's public contract; each branch knows which element it
          // actually renders, and a Ref consumes its element type, so neither side narrows on its own.
          ref={ref as Ref<HTMLAnchorElement>}
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          size={size}
          color={color}
          disabled={disabled}
          loading={loading}
          {...chrome}
          {...(handleClick ? { onClick: handleClick } : {})}
        >
          {children}
        </MuiIconButton>
      );
    }
    return (
      <MuiIconButton
        // Caller props first here too, for one consistent rule about who wins.
        {...aria}
        ref={ref as Ref<HTMLButtonElement>}
        size={size}
        color={color}
        disabled={disabled}
        loading={loading}
        {...chrome}
        {...(handleClick ? { onClick: handleClick } : {})}
      >
        {children}
      </MuiIconButton>
    );
  },
);
